import {
  type EfsaSampnts,
  Matrices,
  PlanSummary,
  type Purpose,
  type SubPurpose,
  type Products,
  type Substances
} from 'prove-common'
import { RJSFSchema } from 'prove-common/rjsf'

interface GetFilterTasksSchemaProps {
  rootPlans: PlanSummary[]
  subPlans: PlanSummary[]
  products: Products
  matrices: Matrices
  sampntTypes: EfsaSampnts
  substances: Substances
  purposes: Purpose[]
  subPurposes: SubPurpose[]
}

type SelectOption = { const: string; title: string }

const withEmptyOption = (options: SelectOption[]): SelectOption[] =>
  options.length > 0 ? options : [{ const: '', title: 'Ingen alternativer' }]

export const getFilterTasksSchema = ({
  rootPlans,
  subPlans,
  products,
  matrices,
  sampntTypes,
  substances,
  purposes,
  subPurposes
}: GetFilterTasksSchemaProps): RJSFSchema => {
  return {
    type: 'object',
    properties: {
      planIds: {
        type: 'string',
        title: 'Plan',
        description:
          'Velg plan for å filtrere oppgaver. Hvis ingen planer er valgt, vises oppgaver for alle planer.',
        oneOf: withEmptyOption(
          rootPlans.map((plan) => ({
            const: plan.id.toString(),
            title: plan.name
          }))
        )
      },
      ...(subPlans.length > 0
        ? {
            subPlanIds: {
              type: 'array',
              title: 'Underplan',
              description:
                'Velg underplan. Når en plan er valgt vises kun dens underplaner.',
              default: [],
              minItems: 0,
              uniqueItems: true,
              items: {
                type: 'string',
                default: '',
                oneOf: subPlans.map((plan) => ({
                  const: plan.id.toString(),
                  title: plan.name
                }))
              }
            }
          }
        : {}),
      productIds: {
        type: 'array',
        title: 'Produkt',
        description:
          'Velg produkt for å filtrere oppgaver. Hvis ingen produkter er valgt, vises oppgaver for alle produkter.',
        default: [],
        minItems: 0,
        maxItems: products.length === 0 ? 0 : undefined,
        readOnly: products.length === 0,
        uniqueItems: true,
        items: {
          type: 'string',
          default: '',
          oneOf: withEmptyOption(
            products.map((product) => ({
              const: product.id.toString(),
              title: product.name
            }))
          )
        }
      },
      matrixIds: {
        type: 'array',
        title: 'Prøve av',
        default: [],
        minItems: 0,
        maxItems: matrices.length === 0 ? 0 : undefined,
        readOnly: matrices.length === 0,
        uniqueItems: true,
        items: {
          type: 'string',
          default: '',
          oneOf: withEmptyOption(
            matrices.map((matrix) => ({
              const: matrix.id.toString(),
              title: matrix.name
            }))
          )
        }
      },
      substanceIds: {
        type: 'array',
        title: 'Analyser for',
        description:
          'Velg analyser for å filtrere oppgaver. Hvis ingen analyser er valgt, vises oppgaver for alle analyser.',
        default: [],
        minItems: 0,
        maxItems: substances.length === 0 ? 0 : undefined,
        readOnly: substances.length === 0,
        uniqueItems: true,
        items: {
          type: 'string',
          default: '',
          oneOf: withEmptyOption(
            substances.map((substance) => ({
              const: substance.id.toString(),
              title: substance.name
            }))
          )
        }
      },
      sampntTypeIds: {
        type: 'array',
        title: 'Uttakssted',
        description:
          'Velg uttakssted for å filtrere oppgaver. Hvis ingen uttakssteder er valgt, vises oppgaver for alle uttakssteder.',
        default: [],
        minItems: 0,
        maxItems: sampntTypes.length === 0 ? 0 : undefined,
        readOnly: sampntTypes.length === 0,
        uniqueItems: true,
        items: {
          type: 'string',
          default: '',
          oneOf: withEmptyOption(
            sampntTypes.map((sampntType) => ({
              const: sampntType.id.toString(),
              title: sampntType.nameNo
            }))
          )
        }
      },
      purposeIds: {
        type: 'array',
        title: 'Hensikt',
        default: [],
        maxItems: purposes.length === 0 ? 0 : undefined,
        readOnly: purposes.length === 0,
        uniqueItems: true,
        items: {
          type: 'string',
          oneOf: withEmptyOption(
            purposes.map((purpose) => ({
              const: purpose.id.toString(),
              title: purpose.name
            }))
          )
        }
      },
      subPurposeIds: {
        type: 'array',
        title: 'Underhensikt',
        default: [],
        maxItems: subPurposes.length === 0 ? 0 : undefined,
        readOnly: subPurposes.length === 0,
        uniqueItems: true,
        items: {
          type: 'string',
          oneOf: withEmptyOption(
            subPurposes.map((subPurpose) => ({
              const: subPurpose.id.toString(),
              title: subPurpose.name
            }))
          )
        }
      }
    }
  }
}
